import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { X, Facebook, Users, Layout, Megaphone, CheckCircle2, ChevronRight, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";

const STEPS = [
  {
    icon: Facebook,
    color: "bg-blue-50 text-blue-600",
    title: "Acesse o Facebook",
    desc: "Abra o Facebook em outra aba do navegador e entre na sua conta normalmente.",
  },
  {
    icon: Users,
    color: "bg-purple-50 text-purple-600",
    title: "Adicione seus grupos",
    desc: 'Clique em "Grupos" no menu lateral, acesse cada grupo e copie o ID da URL (o número que aparece após /groups/).',
  },
  {
    icon: Layout,
    color: "bg-amber-50 text-amber-600",
    title: "Cadastre no painel",
    desc: 'Vá em "Grupos" aqui na plataforma e cole os IDs dos grupos que deseja anunciar.',
  },
  {
    icon: Megaphone,
    color: "bg-green-50 text-green-600",
    title: "Crie sua campanha",
    desc: 'Vá em "Painel", escreva seu post, adicione uma imagem, marque os grupos e clique em "Iniciar Divulgação"!',
  },
];

export default function OnboardingModal() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!user) return;
    const key = `autopost_onboarded_${user.id}`;
    if (!localStorage.getItem(key)) {
      setOpen(true);
    }
  }, [user]);

  const finish = () => {
    const key = `autopost_onboarded_${user.id}`;
    localStorage.setItem(key, "1");
    setOpen(false);
  };

  if (!open) return null;

  const isLast = step === STEPS.length - 1;
  const current = STEPS[step];
  const Icon = current.icon;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm w-full max-w-md shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="bg-zinc-950 text-white px-6 pt-6 pb-8 relative">
          <button
            onClick={finish}
            className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-3 mb-3">
            <div className="h-9 w-9 bg-blue-600 flex items-center justify-center rounded-sm">
              <Zap className="h-4 w-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-bold text-lg">AutoPost BR</span>
          </div>
          <h2 className="font-display text-2xl font-bold leading-tight">
            Seja bem-vindo,{" "}
            <span className="text-blue-400">
              {user?.name?.split(" ")[0]}!
            </span>
          </h2>
          <p className="text-zinc-400 text-sm mt-1">
            Veja como é fácil automatizar suas divulgações 🚀
          </p>
        </div>

        {/* Steps indicator */}
        <div className="flex gap-1.5 px-6 py-4">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition-colors ${
                i <= step ? "bg-blue-600" : "bg-zinc-200"
              }`}
            />
          ))}
        </div>

        {/* Step content */}
        <div className="px-6 pb-6">
          <div className={`h-14 w-14 rounded-sm flex items-center justify-center mb-4 ${current.color}`}>
            <Icon className="h-7 w-7" strokeWidth={1.75} />
          </div>

          <div className="font-mono-label text-zinc-400 mb-1">
            Passo {step + 1} de {STEPS.length}
          </div>
          <h3 className="font-display text-xl font-bold mb-2">{current.title}</h3>
          <p className="text-zinc-600 text-sm leading-relaxed">{current.desc}</p>

          {/* Checklist completo no último passo */}
          {isLast && (
            <div className="mt-4 bg-zinc-50 border border-zinc-200 rounded-sm p-4 space-y-2">
              {STEPS.map((s, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-zinc-700">
                  <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                  <span>{s.title}</span>
                </div>
              ))}
              <div className="pt-2 border-t border-zinc-200 text-sm font-medium text-blue-600">
                🎉 Boas vendas!
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 mt-6">
            {step > 0 && (
              <Button
                variant="outline"
                onClick={() => setStep(step - 1)}
                className="flex-1 rounded-sm h-11"
              >
                Voltar
              </Button>
            )}
            <Button
              onClick={isLast ? finish : () => setStep(step + 1)}
              className="flex-1 rounded-sm h-11 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isLast ? "Começar agora!" : "Próximo"}
              {!isLast && <ChevronRight className="h-4 w-4 ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
